/**
 * OMNI-58645 — visually shrinks fixed-width RTE/email content (nested tables authored
 * at a desktop width, e.g. `width: 600px`) so it fits a narrower container.
 *
 * CSS alone cannot do this: `max-width` never shrinks a `table-layout: auto` table below
 * its min-content width, and a loaded `<img>` contributes its intrinsic width to that
 * min-content figure. So a 600px email stays 600px on a 380px phone and gets clipped.
 *
 * `target` must be an inner element wrapping the bound HTML, and `container` its parent
 * holding `overflow-x: hidden`. Scaling the clipping element itself does NOT work — the
 * clip is applied before the transform, so you get content that is smaller *and* still cut off.
 *
 * Returns a cleanup function that must be called on component destroy.
 */
export function observeScaleToFit(target: HTMLElement, container: HTMLElement): () => void {
  let frame: number | null = null;
  let lastContainerWidth = -1;

  const applyScale = () => {
    frame = null;
    // Reset first so we measure the content's natural size rather than a previous scaling.
    target.style.transform = '';
    target.style.height = '';

    const available = container.clientWidth;
    const naturalWidth = target.scrollWidth;
    if (!available || !naturalWidth) {
      lastContainerWidth = available;
      return;
    }
    lastContainerWidth = available;
    if (naturalWidth <= available) {
      return;
    }

    const scale = available / naturalWidth;
    const naturalHeight = target.scrollHeight;
    target.style.transformOrigin = 'top left';
    target.style.transform = `scale(${scale})`;
    // A transform does not shrink the layout box, so without this the container keeps
    // reserving the unscaled height and leaves a large blank gap / phantom scroll below.
    target.style.height = `${naturalHeight * scale}px`;
  };

  const scheduleScale = () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
    }
    frame = requestAnimationFrame(applyScale);
  };

  // Remote images settle after the first layout pass and only then contribute their
  // intrinsic width, so the first measurement is usually taken before the real width is known.
  const boundImages = new WeakSet<HTMLImageElement>();
  const watchImages = () => {
    target.querySelectorAll('img').forEach((img: HTMLImageElement) => {
      if (img.complete || boundImages.has(img)) {
        return;
      }
      boundImages.add(img);
      img.addEventListener('load', scheduleScale, { once: true });
      img.addEventListener('error', scheduleScale, { once: true });
    });
  };

  const onMutate = () => {
    watchImages();
    scheduleScale();
  };

  const resizeObserver = new ResizeObserver(() => {
    // React to width changes only — our own height write would otherwise re-trigger this.
    if (container.clientWidth !== lastContainerWidth) {
      scheduleScale();
    }
  });
  resizeObserver.observe(container);

  const mutationObserver = new MutationObserver(onMutate);
  mutationObserver.observe(target, { childList: true, subtree: true, characterData: true });

  watchImages();
  scheduleScale();

  return () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
    }
    resizeObserver.disconnect();
    mutationObserver.disconnect();
  };
}
